import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import { toast } from 'sonner';
import axios from 'axios';

const CallContext = createContext(null);

export const useCall = () => {
  const context = useContext(CallContext);
  if (!context) {
    throw new Error('useCall must be used within a CallProvider');
  }
  return context;
};

export const CallProvider = ({ children, token, currentUser, API }) => {
  const [callState, setCallState] = useState({
    isInCall: false,
    isIncoming: false,
    isOutgoing: false,
    isMuted: false,
    isSpeaker: false,
    callId: null,
    otherUser: null,
    callDuration: 0,
    callStatus: 'idle' // idle, ringing, connecting, connected, ended
  });

  const wsRef = useRef(null);
  const peerConnectionRef = useRef(null);
  const localStreamRef = useRef(null);
  const remoteStreamRef = useRef(null);
  const audioRef = useRef(null);
  const ringtoneRef = useRef(null);
  const callTimerRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const httpPollingRef = useRef(null);
  const useHttpFallbackRef = useRef(false);

  // ICE servers configuration with TURN servers for better connectivity
  // Using Metered.ca free TURN servers for NAT traversal
  const iceServers = {
    iceServers: [
      // STUN servers (free, for simple NAT traversal)
      { urls: 'stun:stun.l.google.com:19302' },
      { urls: 'stun:stun1.l.google.com:19302' },
      { urls: 'stun:stun.relay.metered.ca:80' },
      // TURN servers (Metered.ca free tier - for complex NAT/firewall traversal)
      {
        urls: 'turn:global.relay.metered.ca:80',
        username: 'e8dd65c92f6f1a6f7f3d6c1e',
        credential: 'zqLrRkM+aqHJuHl6'
      },
      {
        urls: 'turn:global.relay.metered.ca:80?transport=tcp',
        username: 'e8dd65c92f6f1a6f7f3d6c1e',
        credential: 'zqLrRkM+aqHJuHl6'
      },
      {
        urls: 'turn:global.relay.metered.ca:443',
        username: 'e8dd65c92f6f1a6f7f3d6c1e',
        credential: 'zqLrRkM+aqHJuHl6'
      },
      {
        urls: 'turns:global.relay.metered.ca:443?transport=tcp',
        username: 'e8dd65c92f6f1a6f7f3d6c1e',
        credential: 'zqLrRkM+aqHJuHl6'
      }
    ],
    iceCandidatePoolSize: 10
  };

  // Connect to WebSocket
  const connectWebSocket = useCallback(() => {
    if (!token) return;

    // Construct WebSocket URL - API is like https://xxx.preview.emergentagent.com/api
    // WebSocket needs to be wss://xxx.preview.emergentagent.com/api/ws/call/{token}
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const apiUrl = API.replace(/^https?:\/\//, ''); // Remove protocol
    const wsUrl = `${wsProtocol}//${apiUrl}/ws/call/${token}`;
    
    console.log('Connecting to WebSocket:', wsUrl);

    try {
      wsRef.current = new WebSocket(wsUrl);

      wsRef.current.onopen = () => {
        console.log('Call WebSocket connected');
        // Send ping every 30 seconds to keep connection alive
        const pingInterval = setInterval(() => {
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({ type: 'ping' }));
          }
        }, 30000);
        wsRef.current.pingInterval = pingInterval;
      };

      wsRef.current.onmessage = async (event) => {
        const data = JSON.parse(event.data);
        await handleWebSocketMessage(data);
      };

      wsRef.current.onclose = (event) => {
        console.log('Call WebSocket closed:', event.code, event.reason);
        if (wsRef.current?.pingInterval) {
          clearInterval(wsRef.current.pingInterval);
        }
        // Reconnect after 5 seconds if not intentionally closed
        if (event.code !== 1000) {
          reconnectTimeoutRef.current = setTimeout(connectWebSocket, 5000);
        }
      };

      wsRef.current.onerror = (error) => {
        console.error('Call WebSocket error:', error);
        // Enable HTTP fallback
        useHttpFallbackRef.current = true;
        startHttpPolling();
      };
    } catch (error) {
      console.error('Failed to connect WebSocket:', error);
      // Enable HTTP fallback
      useHttpFallbackRef.current = true;
      startHttpPolling();
    }
  }, [token, API]);

  // HTTP Polling for call signals (always running as fallback)
  const startHttpPolling = useCallback(() => {
    if (httpPollingRef.current) return; // Already polling
    if (!token) return;
    
    console.log('[Call] Starting HTTP polling for call signals');
    httpPollingRef.current = setInterval(async () => {
      if (!token) return;
      
      try {
        const response = await axios.get(`${API}/calls/signal/poll`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const signals = response.data?.signals || [];
        for (const signal of signals) {
          console.log('[Call] Received signal via HTTP:', signal.type);
          // Process each signal type
          switch (signal.type) {
            case 'incoming_call':
              handleIncomingCall(signal);
              break;
            case 'call_answered':
              await handleCallAnsweredFromSignal(signal);
              break;
            case 'call_declined':
              handleCallDeclined(signal);
              break;
            case 'call_ended':
              handleCallEnded();
              break;
            case 'ice_candidate':
              await handleIceCandidate(signal);
              break;
            default:
              console.log('[Call] Unknown signal type:', signal.type);
          }
        }
      } catch (error) {
        // Silently fail - polling will retry
      }
    }, 1500); // Poll every 1.5 seconds for better responsiveness
  }, [token, API]);

  const stopHttpPolling = () => {
    if (httpPollingRef.current) {
      clearInterval(httpPollingRef.current);
      httpPollingRef.current = null;
    }
  };

  // Handle incoming WebSocket messages
  const handleWebSocketMessage = async (data) => {
    switch (data.type) {
      case 'incoming_call':
        handleIncomingCall(data);
        break;
      case 'call_answered':
        await handleCallAnswered(data);
        break;
      case 'call_declined':
        handleCallDeclined(data);
        break;
      case 'call_ended':
        handleCallEnded();
        break;
      case 'call_busy':
        handleCallBusy();
        break;
      case 'call_error':
        handleCallError(data);
        break;
      case 'call_ringing':
        handleCallRinging(data);
        break;
      case 'ice_candidate':
        await handleIceCandidate(data);
        break;
      case 'pong':
        // Keepalive response
        break;
      default:
        console.log('Unknown message type:', data.type);
    }
  };

  // Handle call ringing status (when we initiate a call)
  const handleCallRinging = (data) => {
    if (!data.receiver_online) {
      // User is offline, show notification that call is being sent via push
      toast.info('User is offline. Sending call notification...');
    }
    // Call is ringing (either via WebSocket or push notification)
  };

  // Handle incoming call
  const handleIncomingCall = (data) => {
    // Play ringtone
    if (ringtoneRef.current) {
      ringtoneRef.current.loop = true;
      ringtoneRef.current.play().catch(() => {});
    }

    setCallState({
      isInCall: true,
      isIncoming: true,
      isOutgoing: false,
      isMuted: false,
      isSpeaker: false,
      callId: data.call_id,
      otherUser: {
        email: data.caller_email,
        name: data.caller_name,
        avatar: data.caller_avatar
      },
      callDuration: 0,
      callStatus: 'ringing',
      incomingOffer: data.offer
    });
  };

  // Handle call answered
  const handleCallAnswered = async (data) => {
    try {
      // Stop ringtone
      if (ringtoneRef.current) {
        ringtoneRef.current.pause();
        ringtoneRef.current.currentTime = 0;
      }

      // Set remote description
      if (peerConnectionRef.current && data.answer) {
        await peerConnectionRef.current.setRemoteDescription(
          new RTCSessionDescription(data.answer)
        );
      }

      // Start call timer
      startCallTimer();

      setCallState(prev => ({
        ...prev,
        callStatus: 'connected'
      }));
    } catch (error) {
      console.error('Error handling call answer:', error);
      endCall();
    }
  };

  // Handle call answered from HTTP signal (same as above but for HTTP polling)
  const handleCallAnsweredFromSignal = async (data) => {
    await handleCallAnswered(data);
  };

  // Handle call declined
  const handleCallDeclined = () => {
    stopRingtone();
    toast.error('Call declined');
    cleanup();
    setCallState({
      isInCall: false,
      isIncoming: false,
      isOutgoing: false,
      isMuted: false,
      isSpeaker: false,
      callId: null,
      otherUser: null,
      callDuration: 0,
      callStatus: 'idle'
    });
  };

  // Handle call ended
  const handleCallEnded = () => {
    stopRingtone();
    toast.info('Call ended');
    cleanup();
    setCallState({
      isInCall: false,
      isIncoming: false,
      isOutgoing: false,
      isMuted: false,
      isSpeaker: false,
      callId: null,
      otherUser: null,
      callDuration: 0,
      callStatus: 'idle'
    });
  };

  // Handle call busy
  const handleCallBusy = () => {
    stopRingtone();
    toast.error('User is busy');
    cleanup();
    setCallState({
      isInCall: false,
      isIncoming: false,
      isOutgoing: false,
      isMuted: false,
      isSpeaker: false,
      callId: null,
      otherUser: null,
      callDuration: 0,
      callStatus: 'idle'
    });
  };

  // Handle call error
  const handleCallError = (data) => {
    stopRingtone();
    toast.error(data.error || 'Call failed');
    cleanup();
    setCallState({
      isInCall: false,
      isIncoming: false,
      isOutgoing: false,
      isMuted: false,
      isSpeaker: false,
      callId: null,
      otherUser: null,
      callDuration: 0,
      callStatus: 'idle'
    });
  };

  // Handle ICE candidate
  const handleIceCandidate = async (data) => {
    try {
      if (peerConnectionRef.current && data.candidate) {
        await peerConnectionRef.current.addIceCandidate(
          new RTCIceCandidate(data.candidate)
        );
      }
    } catch (error) {
      console.error('Error adding ICE candidate:', error);
    }
  };

  // Stop ringtone
  const stopRingtone = () => {
    if (ringtoneRef.current) {
      ringtoneRef.current.pause();
      ringtoneRef.current.currentTime = 0;
    }
  };

  // Start call timer
  const startCallTimer = () => {
    callTimerRef.current = setInterval(() => {
      setCallState(prev => ({
        ...prev,
        callDuration: prev.callDuration + 1
      }));
    }, 1000);
  };

  // Cleanup resources
  const cleanup = () => {
    // Stop timer
    if (callTimerRef.current) {
      clearInterval(callTimerRef.current);
      callTimerRef.current = null;
    }

    // Close peer connection
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }

    // Stop local stream
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
      localStreamRef.current = null;
    }

    // Clear remote stream
    remoteStreamRef.current = null;
  };

  // Initiate a call
  const initiateCall = async (receiverEmail, receiverName, receiverAvatar) => {
    // If WebSocket is not connected, try to connect or use HTTP fallback
    const wsConnected = wsRef.current?.readyState === WebSocket.OPEN;
    
    if (!wsConnected && !useHttpFallbackRef.current) {
      toast.info('Connecting to call service...');
      connectWebSocket();
      
      // Wait for connection with timeout
      let attempts = 0;
      while (attempts < 5 && (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN)) {
        await new Promise(resolve => setTimeout(resolve, 500));
        attempts++;
      }
      
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        console.log('[Call] WebSocket failed, using HTTP fallback');
        useHttpFallbackRef.current = true;
        startHttpPolling();
      }
    }

    try {
      // Get user media
      localStreamRef.current = await navigator.mediaDevices.getUserMedia({ audio: true });
      console.log('[WebRTC] Got local audio stream for initiator');

      // Create peer connection
      peerConnectionRef.current = new RTCPeerConnection(iceServers);

      // Monitor ICE connection state for debugging
      peerConnectionRef.current.oniceconnectionstatechange = () => {
        console.log('[WebRTC] ICE state (initiator):', peerConnectionRef.current?.iceConnectionState);
        if (peerConnectionRef.current?.iceConnectionState === 'failed') {
          toast.error('Connection failed. Please try again.');
          endCall();
        }
      };

      // Add local tracks
      localStreamRef.current.getTracks().forEach(track => {
        peerConnectionRef.current.addTrack(track, localStreamRef.current);
      });

      // Handle remote tracks - CRITICAL for audio playback
      peerConnectionRef.current.ontrack = (event) => {
        console.log('[WebRTC] Remote track received (initiator):', event.track.kind);
        remoteStreamRef.current = event.streams[0];
        if (audioRef.current) {
          console.log('[WebRTC] Attaching remote stream to audio element');
          audioRef.current.srcObject = event.streams[0];
          audioRef.current.volume = 1.0;
          audioRef.current.play().catch(err => {
            console.warn('[WebRTC] Autoplay blocked on initiator:', err);
          });
        }
      };

      // Handle ICE candidates - use HTTP if WebSocket fails
      peerConnectionRef.current.onicecandidate = async (event) => {
        if (event.candidate) {
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({
              type: 'ice_candidate',
              call_id: callState.callId,
              target_email: receiverEmail,
              candidate: event.candidate
            }));
          } else if (useHttpFallbackRef.current && token) {
            // HTTP fallback for ICE candidates
            try {
              await axios.post(`${API}/calls/signal/ice`, null, {
                headers: { Authorization: `Bearer ${token}` },
                params: {
                  call_id: callState.callId || 'pending',
                  target_email: receiverEmail
                },
                data: { candidate: event.candidate }
              });
            } catch (e) {
              console.warn('[Call] Failed to send ICE via HTTP:', e);
            }
          }
        }
      };

      // Create offer
      const offer = await peerConnectionRef.current.createOffer();
      await peerConnectionRef.current.setLocalDescription(offer);

      let callId;

      // Send call initiation via WebSocket or HTTP
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        callId = crypto.randomUUID();
        wsRef.current.send(JSON.stringify({
          type: 'call_initiate',
          call_id: callId,
          receiver_email: receiverEmail,
          offer: offer
        }));
      } else {
        // HTTP fallback
        const response = await axios.post(`${API}/calls/signal/initiate`, null, {
          headers: { Authorization: `Bearer ${token}` },
          params: { receiver_email: receiverEmail },
          data: { offer: offer }
        });
        callId = response.data.call_id;
        console.log('[Call] Initiated via HTTP, call_id:', callId);
      }

      // Play ringtone
      if (ringtoneRef.current) {
        ringtoneRef.current.loop = true;
        ringtoneRef.current.play().catch(() => {});
      }

      setCallState({
        isInCall: true,
        isIncoming: false,
        isOutgoing: true,
        isMuted: false,
        isSpeaker: false,
        callId: callId,
        otherUser: {
          email: receiverEmail,
          name: receiverName,
          avatar: receiverAvatar
        },
        callDuration: 0,
        callStatus: 'ringing'
      });

    } catch (error) {
      console.error('Failed to initiate call:', error);
      if (error.name === 'NotAllowedError') {
        toast.error('Microphone permission denied');
      } else {
        toast.error('Failed to start call');
      }
      cleanup();
    }
  };

  // Answer incoming call
  const answerCall = async () => {
    try {
      stopRingtone();

      // Get user media
      localStreamRef.current = await navigator.mediaDevices.getUserMedia({ audio: true });
      console.log('[WebRTC] Got local audio stream for answerer');

      // Create peer connection
      peerConnectionRef.current = new RTCPeerConnection(iceServers);

      // Monitor ICE connection state for debugging
      peerConnectionRef.current.oniceconnectionstatechange = () => {
        console.log('[WebRTC] ICE state (answerer):', peerConnectionRef.current?.iceConnectionState);
        if (peerConnectionRef.current?.iceConnectionState === 'failed') {
          toast.error('Connection failed. Please try again.');
          endCall();
        }
      };

      // Add local tracks
      localStreamRef.current.getTracks().forEach(track => {
        peerConnectionRef.current.addTrack(track, localStreamRef.current);
      });

      // Handle remote tracks - CRITICAL for audio playback
      peerConnectionRef.current.ontrack = (event) => {
        console.log('[WebRTC] Remote track received (answerer):', event.track.kind);
        remoteStreamRef.current = event.streams[0];
        if (audioRef.current) {
          console.log('[WebRTC] Attaching remote stream to audio element');
          audioRef.current.srcObject = event.streams[0];
          audioRef.current.volume = 1.0;
          audioRef.current.play().catch(err => {
            console.warn('[WebRTC] Autoplay blocked on answerer:', err);
          });
        }
      };

      // Handle ICE candidates - use HTTP fallback if needed
      peerConnectionRef.current.onicecandidate = async (event) => {
        if (event.candidate) {
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({
              type: 'ice_candidate',
              call_id: callState.callId,
              target_email: callState.otherUser?.email,
              candidate: event.candidate
            }));
          } else if (useHttpFallbackRef.current && token) {
            try {
              await axios.post(`${API}/calls/signal/ice`, null, {
                headers: { Authorization: `Bearer ${token}` },
                params: {
                  call_id: callState.callId,
                  target_email: callState.otherUser?.email
                },
                data: { candidate: event.candidate }
              });
            } catch (e) {
              console.warn('[Call] Failed to send ICE via HTTP:', e);
            }
          }
        }
      };

      // Set remote description (offer)
      if (callState.incomingOffer) {
        await peerConnectionRef.current.setRemoteDescription(
          new RTCSessionDescription(callState.incomingOffer)
        );
      }

      // Create answer
      const answer = await peerConnectionRef.current.createAnswer();
      await peerConnectionRef.current.setLocalDescription(answer);

      // Send answer via WebSocket or HTTP
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'call_answer',
          call_id: callState.callId,
          caller_email: callState.otherUser?.email,
          answer: answer
        }));
      } else {
        // HTTP fallback
        await axios.post(`${API}/calls/signal/answer`, null, {
          headers: { Authorization: `Bearer ${token}` },
          params: { call_id: callState.callId },
          data: { answer: answer }
        });
        console.log('[Call] Answered via HTTP');
      }

      // Start timer
      startCallTimer();

      setCallState(prev => ({
        ...prev,
        isIncoming: false,
        callStatus: 'connected'
      }));

    } catch (error) {
      console.error('Failed to answer call:', error);
      if (error.name === 'NotAllowedError') {
        toast.error('Microphone permission denied');
      } else {
        toast.error('Failed to answer call');
      }
      declineCall();
    }
  };

  // Decline incoming call
  const declineCall = async () => {
    stopRingtone();

    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'call_decline',
        call_id: callState.callId,
        caller_email: callState.otherUser?.email
      }));
    } else if (useHttpFallbackRef.current && token && callState.callId) {
      // HTTP fallback
      try {
        await axios.post(`${API}/calls/signal/decline`, null, {
          headers: { Authorization: `Bearer ${token}` },
          params: { call_id: callState.callId }
        });
      } catch (e) {
        console.warn('[Call] Failed to decline via HTTP:', e);
      }
    }

    cleanup();
    stopHttpPolling();
    setCallState({
      isInCall: false,
      isIncoming: false,
      isOutgoing: false,
      isMuted: false,
      isSpeaker: false,
      callId: null,
      otherUser: null,
      callDuration: 0,
      callStatus: 'idle'
    });
  };

  // End call
  const endCall = async () => {
    stopRingtone();

    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'call_end',
        call_id: callState.callId,
        other_email: callState.otherUser?.email
      }));
    } else if (useHttpFallbackRef.current && token && callState.callId) {
      // HTTP fallback
      try {
        await axios.post(`${API}/calls/signal/end`, null, {
          headers: { Authorization: `Bearer ${token}` },
          params: { call_id: callState.callId }
        });
      } catch (e) {
        console.warn('[Call] Failed to end call via HTTP:', e);
      }
    }

    cleanup();
    stopHttpPolling();
    setCallState({
      isInCall: false,
      isIncoming: false,
      isOutgoing: false,
      isMuted: false,
      isSpeaker: false,
      callId: null,
      otherUser: null,
      callDuration: 0,
      callStatus: 'idle'
    });
  };

  // Toggle mute
  const toggleMute = () => {
    if (localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setCallState(prev => ({ ...prev, isMuted: !audioTrack.enabled }));
      }
    }
  };

  // Toggle speaker (not really applicable for web, but for UI consistency)
  const toggleSpeaker = () => {
    setCallState(prev => ({ ...prev, isSpeaker: !prev.isSpeaker }));
  };

  // Format duration
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Connect WebSocket on mount and start HTTP polling as backup
  useEffect(() => {
    if (token && currentUser?.role !== 'candidate') {
      // Try WebSocket connection
      connectWebSocket();
      
      // Also start HTTP polling as backup (will receive signals if WebSocket fails)
      // Give WebSocket 3 seconds to connect before starting polling
      const pollingTimeout = setTimeout(() => {
        if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
          console.log('[Call] WebSocket not connected, starting HTTP polling');
          useHttpFallbackRef.current = true;
          startHttpPolling();
        }
      }, 3000);
      
      return () => {
        clearTimeout(pollingTimeout);
      };
    }

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        if (wsRef.current.pingInterval) {
          clearInterval(wsRef.current.pingInterval);
        }
        wsRef.current.close(1000, 'Component unmounting');
      }
      stopHttpPolling();
      cleanup();
    };
  }, [token, currentUser?.role, connectWebSocket, startHttpPolling]);

  // Check for pending incoming call from push notification (when app was closed)
  useEffect(() => {
    const checkPendingCall = () => {
      const pendingCallData = sessionStorage.getItem('pendingIncomingCall');
      if (pendingCallData) {
        try {
          const callData = JSON.parse(pendingCallData);
          // Only process if the call is recent (within last 60 seconds)
          if (Date.now() - callData.timestamp < 60000) {
            console.log('[Call] Found pending incoming call from push notification:', callData);
            // Trigger the incoming call UI
            handleIncomingCall({
              call_id: callData.callId,
              caller_email: callData.senderEmail,
              caller_name: callData.senderName,
              caller_avatar: null,
              offer: callData.offer
            });
          }
          // Clear the pending call
          sessionStorage.removeItem('pendingIncomingCall');
        } catch (e) {
          console.error('[Call] Error parsing pending call data:', e);
          sessionStorage.removeItem('pendingIncomingCall');
        }
      }
    };

    // Check on mount and when tab becomes visible
    checkPendingCall();
    
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkPendingCall();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const value = {
    callState,
    initiateCall,
    answerCall,
    declineCall,
    endCall,
    toggleMute,
    toggleSpeaker,
    formatDuration,
    audioRef,
    ringtoneRef
  };

  // Ensure audio element is properly configured when remote stream arrives
  useEffect(() => {
    if (remoteStreamRef.current && audioRef.current) {
      console.log('[WebRTC] Setting remote stream to audio element');
      audioRef.current.srcObject = remoteStreamRef.current;
      audioRef.current.volume = 1.0;
      // Force play on some browsers
      audioRef.current.play().catch(err => {
        console.warn('[WebRTC] Auto-play blocked, user interaction required:', err);
      });
    }
  }, [callState.callStatus]);

  return (
    <CallContext.Provider value={value}>
      {children}
      {/* Hidden audio elements - critical for WebRTC audio */}
      <audio 
        ref={audioRef} 
        autoPlay 
        playsInline 
        style={{ display: 'none' }}
        onLoadedMetadata={() => console.log('[WebRTC] Audio metadata loaded')}
        onPlay={() => console.log('[WebRTC] Audio playing')}
        onError={(e) => console.error('[WebRTC] Audio error:', e)}
      />
      <audio ref={ringtoneRef} src="/ringtone.mp3" preload="auto" />
    </CallContext.Provider>
  );
};

export default CallContext;
